import { generateItinerary } from './claude';
import { calculateRoute } from './googleMaps';

/**
 * Build complete itinerary with route calculations
 */
export const buildItinerary = async (tripData) => {
  // First, get the LLM-generated plan
  const llmPlan = await generateItinerary(tripData);

  // Enhance with actual route calculations
  const enhancedPlanA = await enhancePlanWithRoutes(llmPlan.plan_a, tripData);
  const enhancedPlanB = await enhancePlanWithRoutes(llmPlan.plan_b, tripData);

  return {
    planA: enhancedPlanA,
    planB: enhancedPlanB,
    reasoning: llmPlan.reasoning,
  };
};

/**
 * Enhance plan with actual Google Maps route data
 */
const enhancePlanWithRoutes = async (plan, tripData) => {
  if (!plan.stops || plan.stops.length === 0) {
    return plan;
  }

  const enhancedStops = [];
  
  // Get place coordinates for route calculation
  const waypoints = plan.stops.map(stop => {
    const place = tripData.places.find(p => p.name === stop.name);
    return {
      name: stop.name,
      coordinates: place?.coordinates || null,
    };
  }).filter(wp => wp.coordinates);

  // Add start location
  waypoints.unshift({
    name: tripData.startLocation.name,
    coordinates: tripData.startLocation.coordinates,
  });

  // Add end location if different
  if (tripData.endLocation && tripData.endLocation.name !== tripData.startLocation.name) {
    waypoints.push({
      name: tripData.endLocation.name,
      coordinates: tripData.endLocation.coordinates,
    });
  }

  // Calculate route if we have enough waypoints
  // Use waypoint optimization to find the most time-efficient route
  let routeData = null;
  let optimizedStops = plan.stops; // Default to original order
  
  if (waypoints.length >= 2) {
    try {
      const shouldOptimize = tripData.optimizationMode === 'optimize_route';
      routeData = await calculateRoute(waypoints, 'WALKING', shouldOptimize);
      
      // If waypoints were optimized, reorder stops to match the optimized route
      // waypoint_order contains indices into the waypointsList (stops only, not origin/destination)
      if (shouldOptimize && routeData.routes && routeData.routes[0] && routeData.routes[0].waypoint_order) {
        const optimizedOrder = routeData.routes[0].waypoint_order;
        // waypoint_order maps to waypointsList indices, which correspond to plan.stops
        optimizedStops = optimizedOrder.map(orderIndex => plan.stops[orderIndex]);
      }
    } catch (error) {
      console.warn('Route calculation failed:', error);
    }
  }

  // Enhance each stop with route data using the optimized order
  for (let i = 0; i < optimizedStops.length; i++) {
    const stop = optimizedStops[i];
    const enhancedStop = { ...stop };

    // If we have route data, use actual travel times
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'itineraryBuilder.js:71',message:'Checking routeData',data:{hasRouteData:!!routeData,hasRoutes:!!routeData?.routes,routeLength:routeData?.routes?.length,currentIndex:i},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    if (routeData && routeData.routes && routeData.routes[0] && routeData.routes[0].legs && i < routeData.routes[0].legs.length) {
      const leg = routeData.routes[0].legs[i];
      enhancedStop.transport_to_next = {
        mode: leg.steps[0]?.travel_mode?.toLowerCase() || 'walk',
        duration_minutes: Math.round(leg.duration.value / 60),
        distance_km: (leg.distance.value / 1000).toFixed(2),
        notes: enhancedStop.transport_to_next?.notes || '',
      };
    }

    enhancedStops.push(enhancedStop);
  }

  return {
    ...plan,
    stops: enhancedStops, // Use optimized order
  };
};

